import React, { useState, useEffect, useRef } from 'react';
import { suppliersAPI, receptionsAPI } from '../services/api';
import './ReceptionModal.css';
import './Modal.css';

// Use the API helper for comparison
const fetchComparison = async (id) => {
  return await receptionsAPI.getComparison(id);
};

function ReceptionModal({ onClose, onSave, reception }) {
  const isEdit = !!reception?.id;
  const [activeTab, setActiveTab] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comparisonData, setComparisonData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const supplierDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    reception_date: reception?.reception_date || '',
    supplier_id: reception?.supplier_id || '',
    supplier_name: reception?.supplier_name || '',
    comments: reception?.comments || ''
  });

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const response = await suppliersAPI.getAll();
        setSuppliers(response.data || response || []);
      } catch (error) {
        console.error('Error loading suppliers:', error);
        setSuppliers([]);
      }
    };
    loadSuppliers();
  }, []);

  useEffect(() => {
    const loadComparison = async () => {
      if (isEdit && reception?.id) {
        try {
          const data = await fetchComparison(reception.id);
          setComparisonData(data);
        } catch (error) {
          console.error('Error loading comparison:', error);
        }
      }
    };
    loadComparison();
  }, [isEdit, reception?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
        setShowSupplierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSupplierSelect = (supplier) => {
    setFormData(prev => ({
      ...prev,
      supplier_id: supplier.suId,
      supplier_name: supplier.suName || supplier.suLegalName
    }));
    setSupplierSearch('');
    setShowSupplierDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validar fecha de recepción
    const dateVal = formData.reception_date; // formato YYYY-MM-DD
    if (dateVal) {
      const parts = dateVal.split('-');
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      const d = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (year < 2020 || year > 2030) {
        alert(`⚠️ El año del ingreso (${year}) parece incorrecto. Por favor revisá la fecha.`);
        return;
      }

      if (month < 1 || month > 12 || day < 1 || day > 31 ||
        d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
        alert('⚠️ La fecha del ingreso no es válida. Por favor revisala.');
        return;
      }

      if (d > today) {
        if (!window.confirm(`⚠️ La fecha del ingreso (${day}/${month}/${year}) es posterior a hoy. ¿Desea continuar?`)) {
          return;
        }
      }
    }

    try {
      setIsSubmitting(true);
      await onSave(formData);
    } catch (error) {
      console.error('Error saving reception:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val);
  };

  const filteredSuppliers = (suppliers || []).filter(s =>
    (s.suName || s.suLegalName || '')?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    (s.suLegalNumber || '')?.includes(supplierSearch)
  ).slice(0, 10);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Editar Ingreso' : 'Nuevo Ingreso'}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>

        {isEdit && (
          <div className="modal-tabs">
            <button
              className={activeTab === 'general' ? 'active' : ''}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
            <button
              className={activeTab === 'comparison' ? 'active' : ''}
              onClick={() => setActiveTab('comparison')}
            >
              Comparación OC vs Recibido
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {activeTab === 'general' && (
              <>
                <div className="form-group">
                  <label>Fecha de Recepción *</label>
                  <input
                    type="date"
                    name="reception_date"
                    value={formData.reception_date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group autocomplete-wrapper" ref={supplierDropdownRef}>
                  <label>Proveedor *</label>
                  <input
                    type="text"
                    className="autocomplete-input"
                    placeholder="Buscar proveedor..."
                    value={formData.supplier_name || supplierSearch}
                    onChange={(e) => {
                      setSupplierSearch(e.target.value);
                      if (formData.supplier_id) {
                        setFormData(prev => ({ ...prev, supplier_id: '', supplier_name: '' }));
                      }
                    }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    required
                    autoComplete="off"
                  />
                  {showSupplierDropdown && filteredSuppliers.length > 0 && (
                    <div className="autocomplete-suggestions">
                      {filteredSuppliers.map(s => (
                        <div key={s.suId} className="autocomplete-suggestion" onClick={() => handleSupplierSelect(s)}>
                          <div className="suggestion-main">
                            <strong>{s.suName || s.suLegalName}</strong>
                            <span className="suggestion-cuit">{s.suLegalNumber}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Comentarios</label>
                  <textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
              </>
            )}

            {activeTab === 'comparison' && (
              <div className="comparison-table-container">
                {comparisonData.length === 0 ? (
                  <p>No hay datos de comparación disponibles. Vincula órdenes de compra para ver la comparación.</p>
                ) : (
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        <th>OC</th>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th className="text-right">Pedido</th>
                        <th className="text-right">Recibido</th>
                        <th className="text-right">Diferencia</th>
                        <th className="text-right">Precio OC</th>
                        <th className="text-right">Imp. Pedido</th>
                        <th className="text-right">Imp. Recibido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((item, idx) => {
                        const diff = (item.received_qty || 0) - (item.ordered_qty || 0);
                        const valueDiff = (item.received_value || 0) - (item.ordered_value || 0);
                        return (
                          <tr key={idx} className={diff !== 0 ? 'highlight-diff' : ''}>
                            <td>#{item.oc_id}</td>
                            <td>{item.article_code}</td>
                            <td>{item.article_description}</td>
                            <td className="text-right">{item.ordered_qty?.toLocaleString('es-AR')}</td>
                            <td className="text-right">{item.received_qty?.toLocaleString('es-AR')}</td>
                            <td className={`text-right ${diff > 0 ? 'text-success' : diff < 0 ? 'text-danger' : ''}`}>
                              {diff > 0 ? '+' : ''}{diff.toLocaleString('es-AR')}
                            </td>
                            <td className="text-right">{formatCurrency(item.unit_price || 0)}</td>
                            <td className="text-right">{formatCurrency(item.ordered_value || 0)}</td>
                            <td className={`text-right ${valueDiff > 0 ? 'text-success' : valueDiff < 0 ? 'text-danger' : ''}`}>
                              {formatCurrency(item.received_value || 0)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="7"><strong>Totales</strong></td>
                        <td className="text-right">
                          <strong>{formatCurrency(comparisonData.reduce((acc, i) => acc + (i.ordered_value || 0), 0))}</strong>
                        </td>
                        <td className="text-right">
                          <strong>{formatCurrency(comparisonData.reduce((acc, i) => acc + (i.received_value || 0), 0))}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            {activeTab === 'general' && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '8px' }}></span>
                    Procesando...
                  </>
                ) : (
                  isEdit ? 'Guardar Cambios' : 'Crear Ingreso'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReceptionModal;
